import * as THREE from 'three';

/**
 * Something from where you actually are, standing past the fence.
 *
 * Two sources, in order:
 * 1. Curated: hand-built landmarks for places that matter (Whitman College's Memorial
 *    Building for Walla Walla), used when the phone is within `radiusKm`.
 * 2. OpenStreetMap: the most notable named building within ~1.2 km, its footprint extruded
 *    to a simple block with its name on a sign. Generic, but it is *your* church / library /
 *    school. Cached per rounded location for a week so the yard doesn't change every open.
 */
interface Curated {
  name: string;
  lat: number;
  lon: number;
  radiusKm: number;
  build: (group: THREE.Group) => void;
}

interface OsmBuilding {
  name: string;
  /** footprint in local metres, already centred */
  pts: [number, number][];
  /** real height, metres */
  height: number;
  ts: number;
}

const STONE = 0xd9d2c3;

export class Landmarks {
  readonly root = new THREE.Group();
  /** what's standing there, for the HUD */
  name: string | null = null;
  onMessage?: (text: string) => void;
  private current: THREE.Group | null = null;
  /** window materials that glow after dark, and a lamp by the door */
  private glow: THREE.MeshStandardMaterial[] = [];
  private lamp = new THREE.PointLight(0xffd9a0, 0, 9, 1.5);
  private lastKey = '';
  /** Whitman: the residence hall standing next to the Memorial Building (Harvey Hall; override in localStorage 'lunaloo.dorm') */
  dorm: string | null = 'Harvey Hall';
  /** world-space door of the dorm and a lawn point in front of it, when the Whitman set is up */
  readonly dormDoor = new THREE.Vector3();
  readonly dormLawn = new THREE.Vector3();
  hasDorm = false;

  constructor(
    scene: THREE.Object3D,
    fence: THREE.Object3D,
  ) {
    // outside the fence, a way back, facing the yard
    this.root.position.set(0, 0, -4.2).applyMatrix4(fence.matrixWorld);
    this.root.rotation.y = fence.rotation.y;
    this.lamp.position.set(0, 1.6, 2.2);
    this.root.add(this.lamp);
    scene.add(this.root);
    try {
      this.dorm = localStorage.getItem('lunaloo.dorm') ?? this.dorm;
    } catch {
      /* no storage */
    }
  }

  private static CURATED: Curated[] = [
    {
      name: 'Whitman College',
      lat: 46.0709,
      lon: -118.3282,
      radiusKm: 8,
      build: (g) => buildWhitman(g),
    },
  ];

  /** force a curated place by name (`?place=whitman`); true if found */
  force(id: string): boolean {
    const c = Landmarks.CURATED.find((x) => x.name.toLowerCase().startsWith(id.toLowerCase()));
    if (!c) return false;
    this.forced = true;
    const g = new THREE.Group();
    c.build(g);
    if (this.dorm && c.name === 'Whitman College') buildDorm(g, this.dorm);
    this.show(g, c.name);
    return true;
  }
  private forced = false;

  /** 'whitman' (default: this is her app, the campus is the backdrop wherever you are) or 'nearby' (the closest notable OSM building) */
  static get preference(): string {
    try {
      return localStorage.getItem('lunaloo.place') ?? 'whitman';
    } catch {
      return 'whitman';
    }
  }

  /** Called whenever we learn where the phone is. */
  async setLocation(lat: number, lon: number): Promise<void> {
    if (this.forced) return;
    if (Landmarks.preference !== 'nearby' && this.force(Landmarks.preference)) return;
    const key = `${lat.toFixed(2)},${lon.toFixed(2)}`;
    if (key === this.lastKey) return;
    this.lastKey = key;
    for (const c of Landmarks.CURATED) {
      if (distanceKm(lat, lon, c.lat, c.lon) <= c.radiusKm) {
        const g = new THREE.Group();
        c.build(g);
        if (this.dorm && c.name === 'Whitman College') buildDorm(g, this.dorm);
        this.show(g, c.name);
        return;
      }
    }
    const b = await this.nearbyBuilding(lat, lon);
    if (!b) return;
    const g = new THREE.Group();
    buildFromFootprint(g, b);
    this.show(g, b.name);
  }

  private show(g: THREE.Group, name: string): void {
    this.hasDorm = false;
    if (this.current) {
      this.root.remove(this.current);
      this.current.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh) m.geometry.dispose();
      });
    }
    g.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        m.castShadow = true;
        m.receiveShadow = true;
      }
    });
    // anything dark and window-like glows at night (the builders tag their window materials)
    this.glow = [];
    g.traverse((o) => {
      o.userData.tapName = 'landmark';
      const m = (o as THREE.Mesh).material as THREE.MeshStandardMaterial | undefined;
      if (m && m.userData.window && !this.glow.includes(m)) this.glow.push(m);
    });
    this.root.add(g);
    this.current = g;
    this.name = name;
    this.root.updateMatrixWorld(true);
    const door = g.getObjectByName('dormDoor');
    if (door) {
      door.getWorldPosition(this.dormDoor);
      // the lawn between the dorm and the Memorial Building's front
      this.dormLawn.set(-3.5, 0, 3.2).applyMatrix4(this.root.matrixWorld);
      this.hasDorm = true;
    }
    this.onMessage?.(`${name} is just past the fence`);
  }

  /** rename the residence hall (care pane); rebuilds the campus if it's the one standing */
  setDorm(name: string): void {
    this.dorm = name || 'Harvey Hall';
    if (this.name !== 'Whitman College' || !this.current) return;
    const g = new THREE.Group();
    buildWhitman(g);
    buildDorm(g, this.dorm);
    this.show(g, 'Whitman College');
  }

  /** 0 day .. 1 night: windows light up and the lamp comes on */
  setNight(n: number): void {
    for (const m of this.glow) {
      m.emissive.set(0xffc978);
      m.emissiveIntensity = 1.6 * n;
    }
    this.lamp.intensity = 25 * n;
  }

  private async nearbyBuilding(lat: number, lon: number): Promise<OsmBuilding | null> {
    const cacheKey = `lunaloo.landmark.${lat.toFixed(2)},${lon.toFixed(2)}`;
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const c = JSON.parse(raw) as OsmBuilding;
        if (Date.now() - c.ts < 7 * 86400_000) return c;
      }
    } catch {
      /* no storage */
    }
    const query = `[out:json][timeout:15];way["building"]["name"](around:1200,${lat.toFixed(5)},${lon.toFixed(5)});out body geom 80;`;
    let elements: OsmWay[];
    try {
      const res = await fetch('https://overpass-api.de/api/interpreter', { method: 'POST', body: 'data=' + encodeURIComponent(query) });
      if (!res.ok) return null;
      elements = ((await res.json()) as { elements: OsmWay[] }).elements ?? [];
    } catch {
      return null;
    }
    const scored = elements
      .filter((w) => w.geometry && w.geometry.length >= 4 && w.tags?.name)
      .map((w) => ({ w, score: notability(w.tags!), area: footprintArea(w.geometry!) }))
      .sort((a, b) => b.score - a.score || b.area - a.area);
    const best = scored[0];
    if (!best) return null;
    const geom = best.w.geometry!;
    const cLat = geom.reduce((s, p) => s + p.lat, 0) / geom.length;
    const cLon = geom.reduce((s, p) => s + p.lon, 0) / geom.length;
    const kx = 111_320 * Math.cos((cLat * Math.PI) / 180);
    const ky = 110_540;
    const pts: [number, number][] = geom.map((p) => [(p.lon - cLon) * kx, (p.lat - cLat) * ky]);
    const t = best.w.tags!;
    const height = parseFloat(t.height ?? '') || (parseInt(t['building:levels'] ?? '', 10) || 2) * 3.2;
    const b: OsmBuilding = { name: t.name!, pts, height, ts: Date.now() };
    try {
      localStorage.setItem(cacheKey, JSON.stringify(b));
    } catch {
      /* no storage */
    }
    return b;
  }
}

interface OsmWay {
  tags?: Record<string, string>;
  geometry?: { lat: number; lon: number }[];
}

/** which nearby building deserves the spot: the university, the church, the library… */
function notability(t: Record<string, string>): number {
  let s = 0;
  const amenity = t.amenity ?? '';
  if (['university', 'college'].includes(amenity)) s += 10;
  if (['place_of_worship', 'townhall', 'library', 'theatre', 'courthouse'].includes(amenity)) s += 8;
  if (['school', 'hospital', 'community_centre', 'arts_centre'].includes(amenity)) s += 6;
  if (t.tourism || t.historic) s += 7;
  if (['church', 'cathedral', 'chapel', 'stadium', 'university', 'civic', 'public', 'train_station'].includes(t.building)) s += 6;
  if (t.building === 'yes' && !amenity) s += 0;
  if (['house', 'residential', 'apartments', 'garage', 'shed', 'industrial', 'warehouse'].includes(t.building)) s -= 5;
  if (t.wikidata || t.wikipedia) s += 5;
  return s;
}

function footprintArea(g: { lat: number; lon: number }[]): number {
  let a = 0;
  for (let i = 0; i < g.length - 1; i++) a += g[i].lon * g[i + 1].lat - g[i + 1].lon * g[i].lat;
  return Math.abs(a) / 2;
}

function distanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const r = Math.PI / 180;
  const dLat = (lat2 - lat1) * r;
  const dLon = (lon2 - lon1) * r;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(dLon / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(a));
}

// ---------- builders ----------

/** a generic block from a real footprint, scaled to fit the backdrop, with its name on a sign */
function buildFromFootprint(g: THREE.Group, b: OsmBuilding): void {
  let maxR = 0;
  for (const [x, z] of b.pts) maxR = Math.max(maxR, Math.hypot(x, z));
  const scale = Math.min(1, 3.2 / Math.max(maxR, 1));
  const shape = new THREE.Shape();
  b.pts.forEach(([x, z], i) => (i === 0 ? shape.moveTo(x * scale, -z * scale) : shape.lineTo(x * scale, -z * scale)));
  shape.closePath();
  const h = THREE.MathUtils.clamp(b.height * scale, 1.6, 4.5);
  const geo = new THREE.ExtrudeGeometry(shape, { depth: h, bevelEnabled: false });
  geo.rotateX(-Math.PI / 2);
  const wall = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: STONE, roughness: 1 }));
  g.add(wall);
  // windows as a band texture would be nicer; a dark base line reads as a plinth for now
  const plinth = new THREE.Mesh(new THREE.ExtrudeGeometry(shape, { depth: 0.12, bevelEnabled: false }), new THREE.MeshStandardMaterial({ color: 0x8a8378, roughness: 1 }));
  plinth.geometry.rotateX(-Math.PI / 2);
  plinth.scale.set(1.03, 1, 1.03);
  g.add(plinth);
  addSign(g, b.name, new THREE.Vector3(0, 0.9, maxR * scale + 0.6));
}

/**
 * Whitman College's Memorial Building, from reference/whitman/buildings.md: a long 3-storey
 * block of pale buff sandstone under a steep terracotta gable roof, the square clock tower
 * rising from the front face a third of the way along with a red pyramid spire, round-arched
 * windows on the top row, two gabled dormers, and an arched doorway at the tower's foot.
 * Yard scale: one storey ≈ 0.9 m.
 */
function buildWhitman(g: THREE.Group): void {
  const stone = new THREE.MeshStandardMaterial({ color: 0xe3d5b5, roughness: 1 });
  const course = new THREE.MeshStandardMaterial({ color: 0xcdbd9a, roughness: 1 });
  const tile = new THREE.MeshStandardMaterial({ color: 0xb8502e, roughness: 1 });
  const win = new THREE.MeshStandardMaterial({ color: 0x2b2a2e, roughness: 0.4 });
  win.userData.window = true;
  const white = new THREE.MeshStandardMaterial({ color: 0xf6f2e8, roughness: 0.9 });
  const W = 7.4;
  const D = 2.4;
  const S = 0.9; // one storey
  const H = 3 * S;
  const body = new THREE.Mesh(new THREE.BoxGeometry(W, H, D), stone);
  body.position.y = H / 2;
  g.add(body);
  // darker coursing lines
  for (const y of [S, 2 * S]) {
    const c = new THREE.Mesh(new THREE.BoxGeometry(W + 0.04, 0.05, D + 0.04), course);
    c.position.y = y;
    g.add(c);
  }
  // steep gable roof: a triangular prism along the block
  const roofH = 1.15;
  const gable = new THREE.Shape();
  gable.moveTo(-D / 2 - 0.15, 0);
  gable.lineTo(D / 2 + 0.15, 0);
  gable.lineTo(0, roofH);
  gable.closePath();
  const roofGeo = new THREE.ExtrudeGeometry(gable, { depth: W + 0.2, bevelEnabled: false });
  roofGeo.rotateY(Math.PI / 2);
  roofGeo.translate(-W / 2 - 0.1, H, 0);
  g.add(new THREE.Mesh(roofGeo, tile));
  // windows: three rows, paired, top row round-arched
  const towerX = -W / 2 + W / 3;
  for (let row = 0; row < 3; row++) {
    const y = S * row + S * 0.55;
    for (let i = 0; i < 12; i++) {
      const x = -W / 2 + 0.45 + i * 0.58 + (i % 2 ? -0.08 : 0.08);
      if (Math.abs(x - towerX) < 0.75) continue; // the tower covers these
      const w = new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.5, 0.04), win);
      w.position.set(x, y, D / 2 + 0.01);
      g.add(w);
      if (row === 2) {
        const arch = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.14, 0.04, 10, 1, false, 0, Math.PI), win);
        arch.rotation.x = Math.PI / 2;
        arch.position.set(x, y + 0.25, D / 2 + 0.01);
        g.add(arch);
      }
    }
  }
  // two gabled dormers on the front slope, each with a triple arched window
  for (const x of [towerX - 2.2, towerX + 2.4]) {
    const dw = 0.9;
    const dormer = new THREE.Mesh(new THREE.BoxGeometry(dw, 0.6, 0.7), stone);
    dormer.position.set(x, H + 0.3, D / 2 - 0.2);
    g.add(dormer);
    const dg = new THREE.Shape();
    dg.moveTo(-dw / 2 - 0.06, 0);
    dg.lineTo(dw / 2 + 0.06, 0);
    dg.lineTo(0, 0.42);
    dg.closePath();
    const dr = new THREE.ExtrudeGeometry(dg, { depth: 0.8, bevelEnabled: false });
    dr.translate(0, 0, -0.75);
    const droof = new THREE.Mesh(dr, tile);
    droof.position.set(x, H + 0.6, D / 2 + 0.2);
    g.add(droof);
    for (const dx of [-0.25, 0, 0.25]) {
      const w = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.3, 0.03), win);
      w.position.set(x + dx, H + 0.28, D / 2 + 0.16);
      g.add(w);
    }
  }
  // the clock tower on the front face, ~2.5× the block, square, same stone
  const T = 1.3;
  const TH = H * 2.4;
  const tz = D / 2 - T / 2 + 0.25;
  const tower = new THREE.Mesh(new THREE.BoxGeometry(T, TH, T), stone);
  tower.position.set(towerX, TH / 2, tz);
  g.add(tower);
  // arched doorway at its foot, with steps
  const door = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.75, 0.06), win);
  door.position.set(towerX, 0.4, tz + T / 2 + 0.01);
  g.add(door);
  const doorArch = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.3, 0.06, 12, 1, false, 0, Math.PI), win);
  doorArch.rotation.x = Math.PI / 2;
  doorArch.position.set(towerX, 0.77, tz + T / 2 + 0.01);
  g.add(doorArch);
  for (let i = 0; i < 3; i++) {
    const st = new THREE.Mesh(new THREE.BoxGeometry(1.3 - i * 0.15, 0.07, 0.28), course);
    st.position.set(towerX, 0.035 + i * 0.07, tz + T / 2 + 0.5 - i * 0.18);
    g.add(st);
  }
  // two small windows, then the triple-arched belfry, then the clock, on each free face
  const faces: [number, number, number][] = [
    [0, 1, 0],
    [1, 0, Math.PI / 2],
    [-1, 0, -Math.PI / 2],
  ];
  const clock = clockTexture();
  for (const [fx, fz, ry] of faces) {
    const place = (m: THREE.Object3D, y: number, off = 0): void => {
      m.position.set(towerX + fx * (T / 2 + 0.01) + fz * off, y, tz + fz * (T / 2 + 0.01) + fx * off);
      m.rotation.y = ry;
      g.add(m);
    };
    for (const y of [1.6, 2.5]) place(new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.4, 0.03), win), y);
    for (const off of [-0.35, 0, 0.35]) {
      const b = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.55, 0.03), win);
      place(b, TH - 1.7, off);
      const a = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.1, 0.03, 10, 1, false, 0, Math.PI), win);
      a.rotation.x = Math.PI / 2;
      place(a, TH - 1.42, off);
      a.rotation.set(Math.PI / 2, 0, 0);
      a.rotateY(ry);
    }
    const c = new THREE.Mesh(new THREE.CircleGeometry(0.4, 24), new THREE.MeshBasicMaterial({ map: clock }));
    place(c, TH - 0.6);
  }
  const spire = new THREE.Mesh(new THREE.ConeGeometry(T * 0.78, 1.9, 4), tile);
  spire.rotation.y = Math.PI / 4;
  spire.position.set(towerX, TH + 0.95, tz);
  g.add(spire);
  const finial = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.4, 6), white);
  finial.position.set(towerX, TH + 2.05, tz);
  g.add(finial);
  // lawn trees and the sign
  for (const x of [-3.6, 3.7]) {
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.09, 0.9, 6), new THREE.MeshStandardMaterial({ color: 0x5a3f2a, roughness: 1 }));
    trunk.position.set(x, 0.45, D / 2 + 1.3);
    const crown = new THREE.Mesh(new THREE.SphereGeometry(0.7, 8, 6), new THREE.MeshStandardMaterial({ color: 0x3e7a3a, roughness: 1 }));
    crown.position.set(x, 1.4, D / 2 + 1.3);
    g.add(trunk, crown);
  }
  addSign(g, 'Whitman College', new THREE.Vector3(towerX, 0.55, D / 2 + 2.0), 0x1e3a5f);
}

/**
 * Harvey Hall (the dorm), from reference/whitman/buildings.md: an L of two 4-storey wings,
 * warm red-brown brick floating on a glass ground floor with tan mullions, flat white roof
 * covered in blue solar panels, tall vertical windows with every second one a projecting box
 * bay, and a 3-storey glass slot at the inside corner over the entrance.
 */
function buildDorm(g: THREE.Group, name: string): void {
  const brick = new THREE.MeshStandardMaterial({ color: 0x8f4b35, roughness: 1 });
  const glass = new THREE.MeshStandardMaterial({ color: 0x7fa7b8, roughness: 0.15, metalness: 0.3 });
  const tan = new THREE.MeshStandardMaterial({ color: 0xc9a86a, roughness: 0.8 });
  const roof = new THREE.MeshStandardMaterial({ color: 0xe8e6e0, roughness: 1 });
  const panel = new THREE.MeshStandardMaterial({ color: 0x1d3a6b, roughness: 0.3, metalness: 0.4 });
  const win = new THREE.MeshStandardMaterial({ color: 0x35424d, roughness: 0.35 });
  win.userData.window = true;
  glass.userData.window = true; // the glass ground floor glows too
  const d = new THREE.Group();
  const S = 0.8; // one storey
  const wingD = 1.3;
  // two wings meeting at the corner (the corner is at the local origin); +x wing and +z wing
  const wings: { len: number; along: 'x' | 'z' }[] = [
    { len: 4.6, along: 'x' },
    { len: 3.4, along: 'z' },
  ];
  for (const w of wings) {
    const sx = w.along === 'x' ? w.len : wingD;
    const sz = w.along === 'x' ? wingD : w.len;
    const cx = w.along === 'x' ? w.len / 2 : 0;
    const cz = w.along === 'x' ? 0 : w.len / 2;
    // glass ground floor, set back a touch
    const base = new THREE.Mesh(new THREE.BoxGeometry(sx - 0.12, S, sz - 0.12), glass);
    base.position.set(cx, S / 2, cz);
    d.add(base);
    // brick upper floors
    const upper = new THREE.Mesh(new THREE.BoxGeometry(sx, 3 * S, sz), brick);
    upper.position.set(cx, S + 1.5 * S, cz);
    d.add(upper);
    // flat roof with a low parapet and solar panels
    const cap = new THREE.Mesh(new THREE.BoxGeometry(sx + 0.06, 0.08, sz + 0.06), roof);
    cap.position.set(cx, 4 * S + 0.04, cz);
    d.add(cap);
    const n = Math.floor(w.len / 0.5);
    for (let i = 0; i < n; i++) {
      const p = new THREE.Mesh(new THREE.BoxGeometry(w.along === 'x' ? 0.4 : wingD - 0.4, 0.04, w.along === 'x' ? wingD - 0.4 : 0.4), panel);
      p.position.set(w.along === 'x' ? 0.3 + i * 0.5 : 0, 4 * S + 0.11, w.along === 'x' ? 0 : 0.3 + i * 0.5);
      p.rotation.x = w.along === 'x' ? 0.15 : 0;
      p.rotation.z = w.along === 'x' ? 0 : -0.15;
      d.add(p);
    }
    // tall windows on the two long faces, every second one a projecting box bay
    const count = Math.floor(w.len / 0.42);
    for (let f = -1; f <= 1; f += 2) {
      for (let i = 0; i < count; i++) {
        const t = 0.35 + i * 0.42;
        if (w.along === 'x' ? t < 0.9 && f === 1 : t < 0.9 && f === -1) continue; // the corner slot lives here
        for (let floor = 1; floor <= 3; floor++) {
          const y = floor * S + S * 0.5;
          const bay = i % 2 === 1;
          const depth = bay ? 0.16 : 0.03;
          const frame = new THREE.Mesh(new THREE.BoxGeometry(w.along === 'x' ? 0.26 : depth, 0.52, w.along === 'x' ? depth : 0.26), bay ? brick : tan);
          const pane = new THREE.Mesh(new THREE.BoxGeometry(w.along === 'x' ? 0.2 : 0.02, 0.44, w.along === 'x' ? 0.02 : 0.2), win);
          const off = (w.along === 'x' ? wingD / 2 : wingD / 2) + depth / 2;
          if (w.along === 'x') {
            frame.position.set(t, y, f * off);
            pane.position.set(t, y, f * (off + depth / 2 + 0.005));
          } else {
            frame.position.set(f * off, y, t);
            pane.position.set(f * (off + depth / 2 + 0.005), y, t);
          }
          d.add(frame, pane);
        }
      }
    }
  }
  // the inside corner: a 3-storey glass slot with the door under it, and the name plaque
  const slot = new THREE.Mesh(new THREE.BoxGeometry(0.9, 3 * S, 0.9), glass);
  slot.position.set(wingD / 2 + 0.35, S + 1.5 * S, wingD / 2 + 0.35);
  d.add(slot);
  for (const [x, z, ry] of [
    [wingD / 2 + 0.35, wingD / 2 + 0.81, 0],
    [wingD / 2 + 0.81, wingD / 2 + 0.35, Math.PI / 2],
  ] as const) {
    const mullion = new THREE.Mesh(new THREE.BoxGeometry(0.05, 3 * S, 0.05), tan);
    mullion.position.set(x, S + 1.5 * S, z);
    mullion.rotation.y = ry;
    d.add(mullion);
  }
  const door = new THREE.Mesh(new THREE.BoxGeometry(0.55, S * 0.85, 0.05), win);
  door.position.set(wingD / 2 + 0.35, S * 0.42, wingD / 2 + 0.82);
  door.name = 'dormDoor';
  d.add(door);
  addSign(d, name, new THREE.Vector3(wingD / 2 + 1.1, S * 0.75, wingD / 2 + 0.03), 0x3d3a35, 0.7);
  // stand it to the left of the Memorial Building, corner toward the yard
  d.position.set(-7.6, 0, -1.2);
  d.rotation.y = 0.35;
  g.add(d);
}

function addSign(g: THREE.Group, text: string, at: THREE.Vector3, color = 0x3d3a35, width = 1.6): void {
  const post = new THREE.Mesh(new THREE.BoxGeometry(width + 0.1, 0.42, 0.08), new THREE.MeshStandardMaterial({ color, roughness: 0.8 }));
  post.position.copy(at);
  g.add(post);
  const face = new THREE.Mesh(new THREE.PlaneGeometry(width, 0.32), new THREE.MeshBasicMaterial({ map: textTexture(text), transparent: true }));
  face.position.copy(at).add(new THREE.Vector3(0, 0, 0.045));
  g.add(face);
  if (at.y > 0.3) {
    const leg = new THREE.Mesh(new THREE.BoxGeometry(0.08, at.y, 0.08), new THREE.MeshStandardMaterial({ color: 0x6b6459, roughness: 1 }));
    leg.position.set(at.x, at.y / 2, at.z - 0.02);
    g.add(leg);
  }
}

function textTexture(text: string): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 104;
  const ctx = c.getContext('2d')!;
  ctx.clearRect(0, 0, c.width, c.height);
  ctx.fillStyle = '#fff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  let size = 54;
  ctx.font = `bold ${size}px -apple-system, system-ui, Georgia, serif`;
  while (ctx.measureText(text).width > c.width - 40 && size > 22) {
    size -= 4;
    ctx.font = `bold ${size}px -apple-system, system-ui, Georgia, serif`;
  }
  ctx.fillText(text, c.width / 2, c.height / 2 + 2);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

function clockTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = 128;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#f4efe2';
  ctx.beginPath();
  ctx.arc(64, 64, 62, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#2b2a2e';
  ctx.lineWidth = 4;
  ctx.stroke();
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(64 + Math.cos(a) * 50, 64 + Math.sin(a) * 50);
    ctx.lineTo(64 + Math.cos(a) * 57, 64 + Math.sin(a) * 57);
    ctx.stroke();
  }
  // hands at the real time
  const now = new Date();
  const h = ((now.getHours() % 12) + now.getMinutes() / 60) / 12;
  const m = now.getMinutes() / 60;
  ctx.lineWidth = 6;
  for (const [f, len] of [
    [h, 30],
    [m, 46],
  ]) {
    const a = f * Math.PI * 2 - Math.PI / 2;
    ctx.beginPath();
    ctx.moveTo(64, 64);
    ctx.lineTo(64 + Math.cos(a) * len, 64 + Math.sin(a) * len);
    ctx.stroke();
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
